import React, { useState } from 'react';
import { X } from 'lucide-react';
import '../styles/AddItemsModal.css';
import '../styles/DrawerForm.css';

interface TarionBulkUpdateModalProps {
  selectedCount: number;
  onUpdate: (dates: {
    tentative1Occupancy?: string | null;
    tentative2Occupancy?: string | null;
    lastPenaltyDate?: string | null;
    finalOccupancy?: string | null;
    firmOccupancy?: string | null;
    possessionDate?: string | null;
  }) => void;
  onCancel: () => void;
}

const TarionBulkUpdateModal: React.FC<TarionBulkUpdateModalProps> = ({
  selectedCount,
  onUpdate,
  onCancel
}) => {
  const [dates, setDates] = useState({
    tentative1Occupancy: '',
    tentative2Occupancy: '',
    lastPenaltyDate: '',
    finalOccupancy: '',
    firmOccupancy: '',
    possessionDate: ''
  });
  const [loading, setLoading] = useState(false);

  const handleDateChange = (field: string, value: string) => {
    setDates({
      ...dates,
      [field]: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Only send dates that have been filled in
    const updateData: any = {};
    if (dates.tentative1Occupancy) updateData.tentative1Occupancy = dates.tentative1Occupancy;
    if (dates.tentative2Occupancy) updateData.tentative2Occupancy = dates.tentative2Occupancy;
    if (dates.lastPenaltyDate) updateData.lastPenaltyDate = dates.lastPenaltyDate;
    if (dates.finalOccupancy) updateData.finalOccupancy = dates.finalOccupancy;
    if (dates.firmOccupancy) updateData.firmOccupancy = dates.firmOccupancy;
    if (dates.possessionDate) updateData.possessionDate = dates.possessionDate;

    // Check if at least one date is filled
    if (Object.keys(updateData).length === 0) {
      alert('Please enter at least one date to update');
      return;
    }

    setLoading(true);
    try {
      await onUpdate(updateData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>
            Update Dates
          </h3>
          <button className="modal-close-btn" onClick={onCancel} disabled={loading}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          <div style={{
            padding: '12px',
            backgroundColor: '#F5E6ED',
            border: '1px solid #E5C9D9',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            <p style={{ fontSize: '14px', color: '#48182F', margin: 0 }}>
              You are updating occupancy dates for <strong>{selectedCount}</strong> selected unit{selectedCount > 1 ? 's' : ''}.
              Only dates you fill in will be updated.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Tentative 1 Occupancy */}
              <div className="form-group">
                <label htmlFor="tentative1Occupancy" className="form-label">
                  Tentative 1 Occupancy
                </label>
                <input
                  type="date"
                  id="tentative1Occupancy"
                  className="form-input"
                  value={dates.tentative1Occupancy}
                  onChange={(e) => handleDateChange('tentative1Occupancy', e.target.value)}
                  disabled={loading}
                />
              </div>

              {/* Tentative 2 Occupancy */}
              <div className="form-group">
                <label htmlFor="tentative2Occupancy" className="form-label">
                  Tentative 2 Occupancy
                </label>
                <input
                  type="date"
                  id="tentative2Occupancy"
                  className="form-input"
                  value={dates.tentative2Occupancy}
                  onChange={(e) => handleDateChange('tentative2Occupancy', e.target.value)}
                  disabled={loading}
                />
              </div>

              {/* Last Penalty Date */}
              <div className="form-group">
                <label htmlFor="lastPenaltyDate" className="form-label">
                  Last Penalty Date
                </label>
                <input
                  type="date"
                  id="lastPenaltyDate"
                  className="form-input"
                  value={dates.lastPenaltyDate}
                  onChange={(e) => handleDateChange('lastPenaltyDate', e.target.value)}
                  disabled={loading}
                />
              </div>

              {/* Final Occupancy */}
              <div className="form-group">
                <label htmlFor="finalOccupancy" className="form-label">
                  Final Occupancy
                </label>
                <input
                  type="date"
                  id="finalOccupancy"
                  className="form-input"
                  value={dates.finalOccupancy}
                  onChange={(e) => handleDateChange('finalOccupancy', e.target.value)}
                  disabled={loading}
                />
              </div>

              {/* Firm Occupancy */}
              <div className="form-group">
                <label htmlFor="firmOccupancy" className="form-label">
                  Firm Occupancy
                </label>
                <input
                  type="date"
                  id="firmOccupancy"
                  className="form-input"
                  value={dates.firmOccupancy}
                  onChange={(e) => handleDateChange('firmOccupancy', e.target.value)}
                  disabled={loading}
                />
              </div>

              {/* Possession Date */}
              <div className="form-group">
                <label htmlFor="possessionDate" className="form-label">
                  Possession Date
                </label>
                <input
                  type="date"
                  id="possessionDate"
                  className="form-input"
                  value={dates.possessionDate}
                  onChange={(e) => handleDateChange('possessionDate', e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="modal-footer" style={{ marginTop: '24px' }}>
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`btn btn-primary btn-md ${loading ? 'btn-loading' : ''}`}
                disabled={loading}
              >
                {loading ? '' : `Update ${selectedCount} Unit${selectedCount > 1 ? 's' : ''}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TarionBulkUpdateModal;
